"use client"

import { Images } from "@/constants/images"
import { Link as LocaleLink } from "@/i18n/routing"
import { isItemActive, linkCategoryPath } from "@/lib/config/lib/helper"
import Image from "next/image"
import { usePathname } from "next/navigation"
import React, { useEffect, useState } from "react"

export interface MenuItem {
    id: string | number;
    title: string;
    url: string;
    attribute?: MenuItem[];
}

interface MobileScrollMenuProps {
    menuItems: MenuItem[]
}

export const MobileScrollMenu: React.FC<MobileScrollMenuProps> = ({ menuItems }) => {
    const pathname = usePathname()
    const [activeSubMenu, setActiveSubMenu] = useState<MenuItem[] | null>(null)
    const [selectedParentId, setSelectedParentId] = useState<string | number | null>(null)
    const [parentData, setParentData] = useState<{ title: string, url: string } | null>(null)

    useEffect(() => {
        if (pathname === "/") {
            setActiveSubMenu(null);
            setSelectedParentId(null);
            setParentData(null);
            return;
        }

        const activeItem = menuItems.find((item) => {
            const isParentActive = isItemActive(item.url, pathname)
            const hasActiveChild = item.attribute?.some((sub: MenuItem) => isItemActive(sub.url, pathname))
            return isParentActive || hasActiveChild;
        });

        if (activeItem) {
            if (activeItem.attribute && activeItem.attribute.length > 0) {
                setActiveSubMenu(activeItem.attribute)
                setSelectedParentId(activeItem.id)
                setParentData({ title: activeItem.title, url: activeItem.url })
            } else {
                setActiveSubMenu(null)
                setSelectedParentId(activeItem.id)
                setParentData(null)
            }
        } else {
            setActiveSubMenu(null)
            setSelectedParentId(null)
            setParentData(null)
        }
    }, [pathname, menuItems])

    const isHomeActive = pathname === "/" && (!selectedParentId || !activeSubMenu);

    return (
        <div className="w-full bg-white shadow-sm">

            <div className="border-b border-[#E5E5EA]">
                <ul
                    className="flex items-center w-full overflow-x-auto no-scrollbar"
                    style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                >
                    <style jsx>{` .no-scrollbar::-webkit-scrollbar { display: none; } `}</style>

                    <li className="flex-shrink-0">
                        <LocaleLink href="/"
                            onClick={() => {
                                setSelectedParentId(null)
                                setActiveSubMenu(null)
                            }}
                            className={`flex items-center justify-center px-4 py-3 h-full border-b-2 ${isHomeActive ? "border-[#008746]" : "border-transparent"}`}>
                            <Image
                                src={isHomeActive ? Images.homepageRedIcon : Images.homepageGrayIcon}
                                alt="Home" width={20} height={20} className="w-5 h-5 object-contain"
                            />
                        </LocaleLink>
                    </li>

                    {menuItems?.map((item) => {
                        if (item.url === "/" || item.title.toLowerCase() === "trang chủ") return null
                        const isActive = selectedParentId === item.id
                        const hasSub = item.attribute && item.attribute.length > 0

                        return (
                            <li key={item.id} className="flex-shrink-0">
                                <LocaleLink
                                    href={linkCategoryPath(item.url)}
                                    onClick={(e) => {

                                        if (hasSub) {
                                            e.preventDefault();
                                            setActiveSubMenu(activeSubMenu === item.attribute ? null : (item.attribute ?? null))
                                            setSelectedParentId(activeSubMenu === item.attribute ? null : item.id)
                                            setParentData({ title: item.title, url: item.url })
                                        }
                                    }}
                                    className={`block px-4 py-3 text-[15px] font-bold transition-colors border-b-2 ${isActive ? "text-[#008746] border-[#008746]" : "text-[#333333] border-transparent"
                                        }`}
                                >
                                    {item.title}
                                </LocaleLink>
                            </li>
                        )
                    })}
                </ul>
            </div>


            {activeSubMenu && activeSubMenu.length > 0 && (
                <div className="bg-[#F8F9FA] border-b border-[#E5E5EA] animate-fadeIn">
                    <ul
                        className="flex items-center w-full overflow-x-auto no-scrollbar"
                        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                    >
                        {parentData && (
                            <li className="flex-shrink-0">
                                <LocaleLink
                                    href={linkCategoryPath(parentData.url)}
                                    className={`block px-4 py-4 text-[14px] font-bold ${isItemActive(parentData.url, pathname) ? "text-[#008746]" : "text-[#333333]"
                                        }`}
                                >
                                    {parentData.title}
                                </LocaleLink>
                            </li>
                        )}
                        {activeSubMenu.map((subItem: MenuItem, index: number) => (
                            <li key={index} className="flex-shrink-0">
                                <LocaleLink
                                    href={linkCategoryPath(subItem.url)}
                                    className={`block px-4 py-4 text-[14px] font-medium ${isItemActive(subItem.url, pathname) ? "text-[#008746]" : "text-[#666666]"
                                        }`}
                                >
                                    {subItem.title}
                                </LocaleLink>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
}
